import { useState } from 'react';
import jwt from 'jsonwebtoken';
import publicIp from 'public-ip';
import { detect } from 'detect-browser';
import queryString from 'query-string';
import * as service from '../../../services/Auth';
import * as submitService from '../../../services/Submit';
import { CodeConstant, StatusConstant, MessageConstant } from '../../../../constants';
import { setIdentity, pushLog2DV2LogServer } from '../../../../services/logging';
import { logLoadTime } from '../../../../services/util/PageLoad';
import { getUserEntranceInformation } from '../../../services/Common';
import { checkVetsmithSubscribe } from '../../../legacy_components/UI_V2_Components/Vetsmith/VetsmithSubscribe';
import fullStoryIdentify from '../../../../services/fullstory';
import clarityIdentify from '../../../../services/clarity';

export function useSignup(authenticate, tokenExpired, location, redirect=true) {
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const getSourceDataFromUrlParams = () => {
    const locationState = location.state || {};
    const sourceUrl = locationState.userVisitStartUrl || window.location.href;
    const urlParams = new URLSearchParams(sourceUrl);
    const sourceName = urlParams.get('s') || 'Onboarding';
    const referrerUrl = locationState.userVisitStartReferrerUrl || document.referrer;
    return { sourceUrl, sourceName, referrerUrl };
  };

  const handleSubmit = async (values, callback) => {
    if (errorMessage) return;
    setLoading(true);
    tokenExpired(false);
    localStorage.setItem('userEmail', values.email);
    const params = {
      email: values.email,
      password: values.password,
      fromWhere: StatusConstant.SIGNUP_EMAIL,
      ...getSourceDataFromUrlParams(),
      entranceData: getUserEntranceInformation('signup'),
    };
    const ref = localStorage.getItem('ref');
    const tempId = localStorage.getItem('tempId');
    if (ref) {
      params.ref = ref;
      if (tempId) params.tempId = tempId;
    }

    try {
      const res = await service.signup(params);
      if (res.data.code === CodeConstant.OK || res.data.code === CodeConstant.CREATED) {
        pushLog2DV2LogServer.info('SEGMENTIO', 'SIGNUP_SUCCESS', {}, { SignupType: StatusConstant.SIGNUP_EMAIL, SSOType: 'N/A', email: values.email });
        const { token, refreshToken, landing } = res.data.data;
        const { accountExists } = res.data;
        localStorage.setItem('token', token);
        localStorage.setItem('refresh_token', refreshToken);
        localStorage.setItem('landing', landing);
        localStorage.setItem('authenticated', 'true');
        authenticate({ authenticated: true });
        if (!landing) localStorage.setItem('showWelcome', 'true');
        fullStoryIdentify();
        clarityIdentify();
        const log_data = { email: values.email, user_id: jwt.decode(token).uid };
        setIdentity(log_data);
        logLoadTime();
        pushLog2DV2LogServer.info('USER_LOGGING', 'SIGN_UP_EMAIL', log_data, { email: params.email, fromWhere: params.fromWhere }, null);
        await saveAgentDataInfo(values.email);
        if (await checkVetsmithSubscribe()) return;
        if (redirect) service.redirectUser(res.data.data.url);
        if(callback && typeof callback === 'function') callback({ accountExists, email: values.email });
      } else {
        setErrorMessage(res.data.message);
      }
    } catch (error) {
      console.error(error)
      setErrorMessage(MessageConstant.NETWORK_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = async (data, callback) => {
    setLoading(true);
    tokenExpired(false);
    localStorage.setItem('userEmail', data.email);
    const fromWhere = StatusConstant.SIGNUP_GOOGLE;
    const loginData = {
      googleId: data.googleId,
      fromWhere: StatusConstant.SIGNUP_GOOGLE,
      entranceData: getUserEntranceInformation('google'),
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      accessToken: data.accessToken,
      ...getSourceDataFromUrlParams(),
    };

    const ref = localStorage.getItem('ref');
    const tempId = localStorage.getItem('tempId');
    if (ref) {
      loginData.ref = ref;
      if (tempId) loginData.tempId = tempId;
    }

    try {
      const res = await service.loginWithGoogle(loginData);
      if (res.data.code === CodeConstant.OK || res.data.code === CodeConstant.CREATED) {
        pushLog2DV2LogServer.info('SEGMENTIO', 'SIGNUP_SUCCESS', {}, { SignupType: 'SSO', SSOType: fromWhere, email: data.email });
        const { token, refreshToken, landing } = res.data.data;
        localStorage.setItem('token', token);
        localStorage.setItem('refresh_token', refreshToken);
        localStorage.setItem('landing', landing);
        localStorage.setItem('authenticated', 'true');
        authenticate({ authenticated: true });
        if (!landing) localStorage.setItem('showWelcome', 'true');
        fullStoryIdentify();
        clarityIdentify();
        const log_data = { email: data.email, user_id: jwt.decode(token).uid };
        setIdentity(log_data);
        logLoadTime();
        pushLog2DV2LogServer.info('USER_LOGGING', `SIGN_UP_GOOGLE`, log_data, loginData, null);
        await saveAgentDataInfo(data.email);
        if (await checkVetsmithSubscribe()) return;
        if (redirect) service.redirectUser(res.data.data.url);
        const { accountExists } = res.data;
        if(callback && typeof callback === 'function') callback({ accountExists, email: data.email});
      } else {
        setErrorMessage(res.data.message);
      }
    } catch (error) {
      console.error(error)
      setErrorMessage(MessageConstant.NETWORK_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const saveAgentDataInfo = async (email) => {
    let agentInfo = localStorage.getItem('agentDataInfo');
    if (!agentInfo) {
      agentInfo = await extractAgentDataInfo();
      agentInfo.noSubmitInfo = true;
    } else {
      agentInfo = JSON.parse(agentInfo);
    }
    agentInfo.email = email || '';
    await submitService.addAgentInfo(agentInfo);
  };

  const extractAgentDataInfo = async () => {
    const browser = detect();
    const ip = await publicIp.v4().catch(() => '');
    const query = queryString.parse(window.location.search);
    return {
      ip,
      referrerUrl: document.referrer || '',
      currentUrl: window.location.href,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height,
      browserName: browser?.name || '',
      browserVersion: browser?.version || '',
      os: browser?.os || '',
      adsSite: query.s || '',
      campaignNum: query.n || '',
      googleStatus: false,
      facebookStatus: false,
      utmSource: query.utm_source || '',
      utmMedium: query.utm_medium || '',
      utmCampaign: query.utm_campaign || '',
      utmContent: query.utm_content || '',
      utmTerm: query.utm_term || '',
    };
  };

  return {
    handleSubmit,
    handleSocialLogin,
    handleErrorMessage: setErrorMessage,
    resetError: () => setErrorMessage(''),
    loading,
    errorMessage,
  };
}
